import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { StatusPip } from "./primitives";

/**
 * Hero — editorial split. Headline + value prop on the left, an
 * always-running "tile viewer" mockup on the right. The mockup is the
 * page's signature visual: animated scan-line sweeping a synthetic
 * drone tile, anomaly polygons drawing in via stroke-dashoffset,
 * coordinate annotations, and a severity readout. Aims to communicate
 * "this is a real instrument, not a generic SaaS" inside three seconds.
 */
export function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative border-b border-[color:var(--color-app-edge)] overflow-hidden">
      {/* Faint grid background */}
      <div className="absolute inset-0 bg-fieldgrid pointer-events-none opacity-60" />

      {/* Top-of-section coordinate band */}
      <div className="relative border-b border-[color:var(--color-app-edge)] bg-[color:var(--color-app-bg)]/50">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10 h-9 flex items-center justify-between gap-6 overflow-hidden">
          <div className="coord-mono flex items-center gap-6 whitespace-nowrap">
            <StatusPip color="emerald" pulse>{t("hero.topband.liveDemo")}</StatusPip>
            <span className="opacity-70 hidden sm:inline">{t("hero.topband.field")}</span>
            <span className="opacity-70 hidden md:inline">{t("hero.topband.coord")}</span>
            <span className="opacity-70 hidden md:inline">{t("hero.topband.gsd")}</span>
            <span className="opacity-70 hidden lg:inline">{t("hero.topband.scan")}</span>
          </div>
          <div className="coord-mono opacity-50 hidden lg:block">{t("hero.topband.env")}</div>
        </div>
      </div>

      <div className="relative mx-auto max-w-[1320px] px-6 lg:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

          {/* ── Headline column ────────────────────────────────────── */}
          <div className="lg:col-span-6 flex flex-col">
            <div className="label-mono mb-6 flex items-center gap-3 lift-in" style={{ animationDelay: "0ms" }}>
              <span className="bracket">00</span>
              <span>{t("hero.kicker")}</span>
            </div>

            <h1
              className="text-display text-[44px] sm:text-[56px] md:text-[72px] lg:text-[80px] lift-in"
              style={{ animationDelay: "120ms" }}
            >
              {t("hero.headline.before")}{" "}
              <span className="text-accent text-accent-amber">
                {t("hero.headline.accent")}
              </span>{" "}
              {t("hero.headline.after")}
            </h1>

            <p
              className="mt-8 max-w-xl text-base md:text-lg text-[color:var(--color-app-text)]/75 leading-relaxed lift-in"
              style={{ animationDelay: "240ms" }}
            >
              {t("hero.subhead.lead")}{" "}
              <em className="not-italic font-medium text-[color:var(--color-app-text)]">
                {t("hero.subhead.everyTile")}
              </em>
              {t("hero.subhead.rest")}
            </p>

            <div
              className="mt-10 flex flex-wrap items-center gap-3 lift-in"
              style={{ animationDelay: "340ms" }}
            >
              <a
                href="/app"
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium bg-[color:var(--color-forest)] text-[color:var(--color-paper)] dark:bg-[color:var(--color-meadow)] dark:text-[color:var(--color-ink)] hover:opacity-90 transition-opacity"
              >
                {t("common.tryFree")}
                <span aria-hidden>→</span>
              </a>
              <a
                href="#vision"
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border border-[color:var(--color-app-edge-2)] hover:bg-[color:var(--color-app-card)] transition-colors"
              >
                {t("common.seeInAction")}
              </a>
            </div>

            {/* Three-up readout strip */}
            <div
              className="mt-14 grid grid-cols-2 gap-0 border-t border-[color:var(--color-app-edge)] pt-6 lift-in"
              style={{ animationDelay: "440ms" }}
            >
              <div>
                <div className="label-mono opacity-60 mb-1.5">{t("hero.readout.runtime")}</div>
                <div className="text-display text-3xl num">
                  {t("hero.readout.runtimeValue")}
                  <span className="opacity-50 text-sm ml-1 font-normal">{t("hero.readout.runtimeUnit")}</span>
                </div>
              </div>
              <div className="pl-5 border-l border-[color:var(--color-app-edge)]">
                <div className="label-mono opacity-60 mb-1.5">{t("hero.readout.kinds")}</div>
                <div className="text-display text-3xl num">
                  {t("hero.readout.kindsValue")}
                  <span className="opacity-50 text-sm ml-1 font-normal">{t("hero.readout.kindsUnit")}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Tile viewer column ─────────────────────────────────── */}
          <div className="lg:col-span-6 relative">
            <TileViewer />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

function TileViewer() {
  const { t } = useTranslation();
  return (
    <div className="relative lift-in" style={{ animationDelay: "260ms" }}>
      {/* Crosshair tick on top-left corner of viewer */}
      <div className="absolute -top-3 -left-3 coord-mono opacity-60">+</div>
      <div className="absolute -top-3 right-0 coord-mono opacity-60">+</div>

      <div className="relative border border-[color:var(--color-app-edge-2)] bg-[color:var(--color-app-card)] backdrop-blur">
        {/* Viewer chrome — top */}
        <div className="flex items-center justify-between gap-4 px-3 py-2 border-b border-[color:var(--color-app-edge)] bg-[color:var(--color-app-bg)]/70">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[color:var(--color-amber)]" />
            <span className="label-mono">{t("hero.viewer.tile")}</span>
          </div>
          <div className="hidden sm:flex items-center gap-3 coord-mono opacity-70">
            <span>{t("hero.viewer.zoom")}</span>
            <span>·</span>
            <span>{t("hero.viewer.epsg")}</span>
            <span>·</span>
            <span>{t("hero.viewer.anomalies")}</span>
          </div>
        </div>

        {/* Tile canvas */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <SyntheticTile />

          {/* Tile grid overlay + anomaly polygons */}
          <svg
            viewBox="0 0 800 600"
            className="absolute inset-0 w-full h-full pointer-events-none"
            preserveAspectRatio="none"
          >
            {Array.from({ length: 7 }).map((_, i) => (
              <line key={`v${i}`} x1={(i + 1) * 100} y1={0} x2={(i + 1) * 100} y2={600} stroke="rgba(255,255,255,0.05)" strokeWidth={1} />
            ))}
            {Array.from({ length: 5 }).map((_, i) => (
              <line key={`h${i}`} x1={0} y1={(i + 1) * 100} x2={800} y2={(i + 1) * 100} stroke="rgba(255,255,255,0.05)" strokeWidth={1} />
            ))}

            {/* Anomaly polygons — stagger draw-in */}
            <polygon
              points="180,140 280,120 320,210 220,250 170,210"
              fill="rgba(217, 119, 6, 0.22)"
              stroke="#fbbf24"
              strokeWidth={2}
              className="draw-poly"
              style={{ "--dash-len": "440", animationDelay: "0s" } as CSSProperties}
            />
            <polygon
              points="510,80 620,90 640,180 540,200"
              fill="rgba(208, 92, 117, 0.32)"
              stroke="#ef8aa1"
              strokeWidth={2}
              className="draw-poly"
              style={{ "--dash-len": "380", animationDelay: "0.4s" } as CSSProperties}
            />
            <polygon
              points="120,420 240,430 260,510 140,520"
              fill="rgba(30, 138, 158, 0.28)"
              stroke="#5fb3c4"
              strokeWidth={2}
              className="draw-poly"
              style={{ "--dash-len": "420", animationDelay: "0.8s" } as CSSProperties}
            />
            <polygon
              points="600,420 700,400 720,490 620,510"
              fill="rgba(199, 154, 47, 0.30)"
              stroke="#ecc36a"
              strokeWidth={2}
              className="draw-poly"
              style={{ "--dash-len": "380", animationDelay: "1.2s" } as CSSProperties}
            />

            {/* Crosshair markers on polygon centroids */}
            <g stroke="rgba(232, 239, 231, 0.7)" strokeWidth={1}>
              {[
                { x: 240, y: 185 },
                { x: 580, y: 140 },
                { x: 190, y: 470 },
                { x: 660, y: 455 },
              ].map((p, i) => (
                <g key={i}>
                  <line x1={p.x - 7} y1={p.y} x2={p.x - 2} y2={p.y} />
                  <line x1={p.x + 2} y1={p.y} x2={p.x + 7} y2={p.y} />
                  <line x1={p.x} y1={p.y - 7} x2={p.x} y2={p.y - 2} />
                  <line x1={p.x} y1={p.y + 2} x2={p.x} y2={p.y + 7} />
                  <circle cx={p.x} cy={p.y} r={1.5} fill="rgba(232, 239, 231, 0.9)" />
                </g>
              ))}
            </g>
          </svg>

          {/* Vertical scan line */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="scan-line h-full w-full relative">
              <div
                className="absolute left-0 right-0 top-0 h-px bg-[color:var(--color-meadow)]/90"
                style={{ boxShadow: "0 0 18px 2px rgba(168, 200, 120, 0.65)" }}
              />
              <div
                className="absolute left-0 right-0 top-0 h-12 -translate-y-12"
                style={{ background: "linear-gradient(to bottom, transparent, rgba(168, 200, 120, 0.10))" }}
              />
            </div>
          </div>

          {/* Floating annotation chips */}
          <AnnotChip x="50%" y="3%" align="center">
            <StatusPip color="amber" pulse>{t("hero.viewer.chipStress")}</StatusPip>
          </AnnotChip>
          <AnnotChip x="86%" y="11%" align="end">
            <StatusPip color="coral" pulse>{t("hero.viewer.chipPest")}</StatusPip>
          </AnnotChip>
          <AnnotChip x="40%" y="93%" align="start">
            <StatusPip color="water" pulse>{t("hero.viewer.chipIrrig")}</StatusPip>
          </AnnotChip>
          <AnnotChip x="92%" y="71%" align="end">
            <StatusPip color="saffron">{t("hero.viewer.chipWeeds")}</StatusPip>
          </AnnotChip>
        </div>

        {/* Viewer chrome — bottom */}
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-[color:var(--color-app-edge)] bg-[color:var(--color-app-bg)]/70 coord-mono">
          <span className="opacity-70">{t("hero.viewer.confidence")}</span>
          <span className="hidden sm:inline opacity-70">{t("hero.viewer.vision")}</span>
          <span className="opacity-70">{t("hero.viewer.delta")}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * SyntheticTile — pure-SVG "what a drone tile looks like":
 * three diagonal field strips with row striations and subtle
 * colour variation; one tree-blob; a path/road for character.
 * Pre-rendered so animation only happens above (scan + polygons).
 */
function SyntheticTile() {
  return (
    <svg viewBox="0 0 800 600" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="hero-strip-a" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#446b35" />
          <stop offset="50%" stopColor="#5a8543" />
          <stop offset="100%" stopColor="#3d5c2d" />
        </linearGradient>
        <linearGradient id="hero-strip-b" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#6a9748" />
          <stop offset="50%" stopColor="#4d7235" />
          <stop offset="100%" stopColor="#5e8c3f" />
        </linearGradient>
        <linearGradient id="hero-strip-c" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#7da557" />
          <stop offset="60%" stopColor="#5a823d" />
          <stop offset="100%" stopColor="#476830" />
        </linearGradient>
        {/* Stress region — slightly amber-shifted */}
        <radialGradient id="hero-stress" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"  stopColor="rgba(180, 110, 50, 0.55)" />
          <stop offset="70%" stopColor="rgba(180, 110, 50, 0.10)" />
          <stop offset="100%" stopColor="rgba(180, 110, 50, 0)" />
        </radialGradient>
        <pattern id="hero-rows" patternUnits="userSpaceOnUse" width="8" height="800" patternTransform="rotate(-6)">
          <line x1="0" y1="0" x2="0" y2="800" stroke="rgba(20, 30, 10, 0.18)" strokeWidth="1.2" />
        </pattern>
      </defs>

      {/* Base soil colour */}
      <rect x="0" y="0" width="800" height="600" fill="#1e2a14" />

      {/* Three angled crop strips */}
      <polygon points="0,40 800,0 800,200 0,260" fill="url(#hero-strip-a)" />
      <polygon points="0,260 800,200 800,380 0,440" fill="url(#hero-strip-b)" />
      <polygon points="0,440 800,380 800,580 0,600" fill="url(#hero-strip-c)" />

      {/* Row striations across the whole tile */}
      <rect x="0" y="0" width="800" height="600" fill="url(#hero-rows)" opacity="0.6" />

      {/* Stress radial blooms behind the amber polygon */}
      <ellipse cx="250" cy="185" rx="160" ry="100" fill="url(#hero-stress)" />
      <ellipse cx="580" cy="140" rx="120" ry="90"  fill="url(#hero-stress)" opacity="0.85" />

      {/* Tree / shrub blob */}
      <g opacity="0.85">
        <circle cx="430" cy="320" r="22" fill="#1b3014" />
        <circle cx="445" cy="305" r="16" fill="#264d20" />
        <circle cx="420" cy="335" r="14" fill="#264d20" />
      </g>

      {/* Diagonal farm track (lighter line) */}
      <path
        d="M -10 525 Q 200 480 420 470 T 820 430"
        fill="none"
        stroke="rgba(190, 170, 120, 0.45)"
        strokeWidth="6"
      />
      <path
        d="M -10 525 Q 200 480 420 470 T 820 430"
        fill="none"
        stroke="rgba(230, 215, 175, 0.25)"
        strokeWidth="2"
      />

      {/* Subtle vignette */}
      <radialGradient id="hero-vignette" cx="0.5" cy="0.5" r="0.7">
        <stop offset="60%" stopColor="rgba(0,0,0,0)" />
        <stop offset="100%" stopColor="rgba(0,0,0,0.35)" />
      </radialGradient>
      <rect x="0" y="0" width="800" height="600" fill="url(#hero-vignette)" />
    </svg>
  );
}

function AnnotChip({
  x,
  y,
  align,
  children,
}: {
  x: string;
  y: string;
  align: "start" | "center" | "end";
  children: ReactNode;
}) {
  const transform =
    align === "center" ? "translate(-50%, 0)" :
    align === "end"    ? "translate(-100%, 0)" :
                          "translate(0, 0)";
  return (
    <div
      className="absolute px-2 py-1 bg-[color:var(--color-app-bg)]/85 border border-[color:var(--color-app-edge-2)] backdrop-blur-sm"
      style={{ left: x, top: y, transform }}
    >
      {children}
    </div>
  );
}
